/** SUNTRONIX ONE — public CSAT feedback page (email link, no auth). */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const RATING = [[5, "Very Good"], [4, "Good"], [3, "Average"], [2, "Poor"], [1, "Very Poor"]];
const TINT = { 5: "bg-emerald-600", 4: "bg-teal-600", 3: "bg-amber-500", 2: "bg-orange-600", 1: "bg-rose-600" };

export default function CsatPublic() {
  const { token } = useParams();
  const [req, setReq] = useState(null);
  const [done, setDone] = useState(false);
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState("");
  const [nps, setNps] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/csat/${token}`)
      .then(({ data }) => { setReq(data.request); setDone(data.already_responded); })
      .catch(() => setErr("This feedback link is invalid or has expired."));
  }, [token]);

  const submit = async () => {
    if (!rating) return;
    try {
      await axios.post(`${BACKEND}/api/public/csat/${token}/respond`,
        { rating, comment: comment || null, nps_score: nps });
      setDone(true);
    } catch (e) { setErr(e.response?.data?.detail || "Could not submit"); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="csat-public-page">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl ring-1 ring-slate-200">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-teal-700">SUNTRONIX ONE</div>
        {err ? (
          <div className="mt-4 text-[13px] text-rose-600" data-testid="csat-public-error">{err}</div>
        ) : done ? (
          <div className="mt-4 text-center" data-testid="csat-public-thanks">
            <div className="text-2xl">🙏</div>
            <div className="mt-2 text-[15px] font-semibold text-slate-900">Thank you for your feedback!</div>
            <div className="text-[12px] text-slate-500">Your response helps us serve you better.</div>
          </div>
        ) : !req ? (
          <div className="mt-4 text-[13px] text-slate-400">Loading…</div>
        ) : (
          <>
            <div className="mt-1 text-[18px] font-semibold text-slate-900">How was your experience?</div>
            <div className="text-[12px] text-slate-500">{req.service_type} {req.reference ? `· ${req.reference}` : ""}</div>
            <div className="mt-4 space-y-2">
              {RATING.map(([v, l]) => (
                <button key={v} onClick={() => setRating(v)} data-testid={`csat-rate-${v}`}
                  className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left text-[13px] font-medium transition-all ${rating === v ? "border-transparent text-white " + TINT[v] : "border-slate-200 text-slate-700 hover:border-teal-300"}`}>
                  <span className="text-lg">{["😡", "🙁", "😐", "🙂", "😃"][v - 1]}</span> {l}
                </button>
              ))}
            </div>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Add a comment (optional)"
              className="mt-3 w-full rounded-md border border-slate-200 px-3 py-2 text-[13px]" data-testid="csat-comment" />
            <div className="mt-3">
              <div className="text-[11px] text-slate-500">How likely are you to recommend us? (0–10)</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {Array.from({ length: 11 }).map((_, i) => (
                  <button key={i} onClick={() => setNps(i)} data-testid={`csat-nps-${i}`}
                    className={`h-7 w-7 rounded text-[11px] font-semibold ${nps === i ? "bg-teal-700 text-white" : "bg-slate-100 text-slate-600"}`}>{i}</button>
                ))}
              </div>
            </div>
            <button onClick={submit} disabled={!rating} data-testid="csat-public-submit"
              className="mt-4 w-full rounded-lg bg-teal-800 py-2.5 text-[13px] font-semibold text-white disabled:opacity-40">
              Submit feedback
            </button>
          </>
        )}
      </div>
    </div>
  );
}
